import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  ExternalLink,
  Globe,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
} from 'lucide-react';
import {
  AdminPage,
  IconTile,
  PageHeader,
  Panel,
  StatusChip,
  adminButton,
  useAdminPageTitle,
} from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import {
  buildDnsInstructions,
  canonicaliseCustomDomain,
  domainInputError,
  stageCopy,
  type DnsRecord,
} from '@/lib/customDomain';
import {
  PlanUpgradeRequiredError,
  domainStage,
  useAddDomain,
  useCustomDomainEntitlement,
  useDomainCheck,
  useDomainsData,
  useRemoveDomain,
  type CustomDomain,
} from '@/hooks/useDomainAdmin';

const getErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') return error.message;
  return 'Something went wrong. Please try again.';
};

const addDomainSchema = z.object({
  domain: z.string().superRefine((value, ctx) => {
    const message = domainInputError(value);
    if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  }),
});
type AddDomainValues = z.infer<typeof addDomainSchema>;

function useCopy() {
  const { toast } = useToast();
  return (text: string, what = 'Copied') => {
    void navigator.clipboard?.writeText(text);
    toast({ title: what });
  };
}

function formatChecked(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const RECORD_PURPOSE: Record<DnsRecord['purpose'], string> = {
  verify: 'Proves you own the domain',
  apex: 'Points your domain at your site',
  www: 'Points the www version at your site',
  subdomain: 'Points this address at your site',
  'vercel-verify': 'Extra check from our hosting provider',
};

function CopyField({ label, value, onCopy }: { label: string; value: string; onCopy: (v: string, what: string) => void }) {
  return (
    <div className="min-w-0">
      <p className="adm-label mb-1">{label}</p>
      <div className="flex items-center gap-1.5">
        <code className="min-w-0 flex-1 break-all rounded-md bg-muted px-2 py-1.5 font-mono text-[12.5px] text-foreground">
          {value}
        </code>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={adminButton.iconGhost}
          onClick={() => onCopy(value, `${label} copied`)}
          aria-label={`Copy ${label.toLowerCase()} ${value}`}
        >
          <Copy />
        </Button>
      </div>
    </div>
  );
}

function DnsRecordsList({ records, done }: { records: DnsRecord[]; done: Set<string> }) {
  const copy = useCopy();
  return (
    <ol className="space-y-3" data-testid="dns-records">
      {records.map((r) => (
        <li key={r.key} className="rounded-lg border border-border bg-card p-3" data-testid={`dns-record-${r.key}`}>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[12px] font-semibold text-secondary-foreground">{r.type}</span>
            <span className="text-[13px] font-medium text-foreground">{RECORD_PURPOSE[r.purpose]}</span>
            {r.purpose === 'vercel-verify' && <StatusChip tone="violet" dot={false}>Extra</StatusChip>}
            {done.has(r.key) && (
              <span className="ml-auto inline-flex items-center gap-1 text-[12px] font-medium text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-3.5 w-3.5" /> Found
              </span>
            )}
          </div>
          <div className="grid gap-2 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
            <CopyField label="Name / Host" value={r.name} onCopy={copy} />
            <CopyField label="Value" value={r.value} onCopy={copy} />
          </div>
          {r.name !== r.fqdn && (
            <p className="mt-2 text-[12px] text-muted-foreground">
              If your provider asks for the full name, use <span className="font-mono">{r.fqdn}</span>.
            </p>
          )}
        </li>
      ))}
    </ol>
  );
}

function DomainCard({ orgId, domain, canWrite, onRemove }: {
  orgId: string;
  domain: CustomDomain;
  canWrite: boolean;
  onRemove: () => void;
}) {
  const check = useDomainCheck(canWrite ? orgId : null, domain);
  const stage = domainStage(domain);
  const reason = domain.dns_config?.reason ?? null;
  const copy = stageCopy(stage, domain.domain, { reason, includeWww: domain.include_www });
  const upgradeNeeded = check.data?.ok === false && check.data.code === 'plan_upgrade_required';

  const records = useMemo(
    () =>
      buildDnsInstructions({
        domain: domain.domain,
        includeWww: domain.include_www,
        token: domain.verification_token,
        ipv4: domain.dns_config?.ipv4,
        cname: domain.dns_config?.cname,
        vercelChallenges: domain.dns_config?.challenges,
      }),
    [domain],
  );

  const done = useMemo(() => {
    const s = new Set<string>();
    if (domain.verification_status === 'verified') s.add('verify');
    const hosts = domain.dns_config?.hosts ?? [];
    if (hosts[0]?.misconfigured === false) s.add(domain.include_www ? 'apex' : 'subdomain');
    if (hosts[1]?.misconfigured === false) s.add('www');
    return s;
  }, [domain]);

  const checking = check.isFetching;
  const lastChecked = formatChecked(domain.last_checked_at);
  const wwwPending = stage === 'live' && domain.include_www && domain.dns_config?.hosts?.[1]?.misconfigured === true;

  return (
    <Panel
      data-testid="custom-domain-card"
      title={<span className="break-all font-mono">{domain.domain}</span>}
      icon={<Globe className="h-4 w-4 text-muted-foreground" />}
      actions={<StatusChip tone={copy.tone} data-testid="domain-status">{copy.label}</StatusChip>}
    >
      <div className="space-y-5">
        <div
          className={cn(
            'rounded-lg border p-3.5',
            stage === 'live' && 'border-green-200 bg-green-50/60 dark:border-green-900 dark:bg-green-950/30',
            stage === 'problem' && 'border-destructive/30 bg-destructive/[0.05]',
            stage !== 'live' && stage !== 'problem' && 'border-border bg-muted/40',
          )}
        >
          <p className="text-[14px] font-semibold text-foreground" data-testid="domain-status-title">{copy.title}</p>
          <p className="mt-1 text-[13px] text-muted-foreground">{copy.body}</p>
          {stage === 'problem' && domain.provisioning_error && (
            <p className="mt-2 flex items-start gap-2 text-[13px] text-foreground" data-testid="domain-problem">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <span>{domain.provisioning_error}</span>
            </p>
          )}
          {wwwPending && (
            <p className="mt-2 text-[13px] text-muted-foreground">
              www.{domain.domain} isn’t pointing at us yet — check the CNAME record for <span className="font-mono">www</span> below.
            </p>
          )}
          {upgradeNeeded && (
            <p className="mt-2 text-[13px] text-foreground">
              {check.data?.message}{' '}
              <Link to="/admin/billing" className="font-semibold text-primary underline-offset-2 hover:underline">See plans</Link>
            </p>
          )}
          {check.isError && (
            <p className="mt-2 text-[13px] text-destructive">We couldn’t run the check just now: {getErrorMessage(check.error)}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {stage === 'live' ? (
              <Button asChild size="sm" variant="outline" className={adminButton.outline}>
                <a href={`https://${domain.domain}`} target="_blank" rel="noreferrer"><ExternalLink /> Visit your site</a>
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={adminButton.outline}
              onClick={() => void check.refetch()}
              disabled={checking || !canWrite}
            >
              {checking ? <Loader2 className="animate-spin" /> : <RefreshCw />}
              {checking ? 'Checking…' : 'Check now'}
            </Button>
            <span className="text-[12px] text-muted-foreground" aria-live="polite">
              {checking ? 'Checking your domain…' : lastChecked ? `Last checked ${lastChecked}` : 'Not checked yet'}
            </span>
          </div>
        </div>

        {stage !== 'live' && (
          <div className="space-y-3">
            <h3 className="text-[14px] font-semibold text-foreground">How to connect it</h3>
            <ol className="list-decimal space-y-1.5 pl-5 text-[13px] text-muted-foreground">
              <li>Sign in to the company you bought your domain from (for example GoDaddy, 123 Reg, IONOS, Namecheap or Cloudflare) and open the DNS settings.</li>
              <li>Add each record below. If there’s already an <span className="font-mono">A</span> record for <span className="font-mono">@</span> or a <span className="font-mono">CNAME</span> for <span className="font-mono">www</span>, edit it rather than adding a second one.</li>
              <li>Using Cloudflare? Set these records to “DNS only” (grey cloud).</li>
              <li>That’s it — we check automatically. DNS changes can take up to 24 hours to show up.</li>
            </ol>
            <DnsRecordsList records={records} done={done} />
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <p className="text-[12.5px] text-muted-foreground">
            {domain.include_www
              ? `We set up both ${domain.domain} and www.${domain.domain} — www forwards to ${domain.domain}.`
              : `We set up ${domain.domain} only.`}
          </p>
          <Button type="button" size="sm" variant="outline" className={adminButton.danger} onClick={onRemove} disabled={!canWrite}>
            <Trash2 /> Remove domain
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function UpgradePanel() {
  return (
    <Panel data-testid="custom-domain-upgrade">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <IconTile tone="violet" className="h-11 w-11 rounded-xl [&_svg]:h-5 [&_svg]:w-5"><Lock /></IconTile>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-foreground">Use your own domain on Team or Business</p>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Put your site on an address like yourbusiness.co.uk. We sort the SSL padlock and routing for you.
            Your free thepolishedversion.com address keeps working either way.
          </p>
        </div>
        <Button asChild size="sm" className={adminButton.primary}>
          <Link to="/admin/billing"><Sparkles /> See plans</Link>
        </Button>
      </div>
    </Panel>
  );
}

function AddDomainForm({ onAdd, saving, disabled }: { onAdd: (domain: string) => Promise<void>; saving: boolean; disabled: boolean }) {
  const form = useForm<AddDomainValues>({ resolver: zodResolver(addDomainSchema), defaultValues: { domain: '' } });
  const typed = form.watch('domain');
  const preview = canonicaliseCustomDomain(typed ?? '');

  const submit = form.handleSubmit(async ({ domain }) => {
    const c = canonicaliseCustomDomain(domain);
    if (!c.ok) return;
    await onAdd(c.domain);
    form.reset({ domain: '' });
  });

  return (
    <Panel
      title="Connect your own domain"
      description="Already own a domain? Point it at your site in a few steps."
    >
      <Form {...form}>
        <form onSubmit={(e) => void submit(e)} className="space-y-3" noValidate>
          <FormField
            control={form.control}
            name="domain"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Your domain</FormLabel>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <FormControl>
                    <Input placeholder="yourbusiness.co.uk" autoComplete="off" autoCapitalize="none" spellCheck={false} {...field} />
                  </FormControl>
                  <Button type="submit" className={adminButton.primary} disabled={saving || disabled}>
                    {saving ? <Loader2 className="animate-spin" /> : <Plus />}
                    Connect domain
                  </Button>
                </div>
                <FormDescription>
                  {preview.ok
                    ? preview.includeWww
                      ? `We’ll set up ${preview.domain} and www.${preview.domain} (www forwards to ${preview.domain}).`
                      : `We’ll set up ${preview.domain}.`
                    : 'Just the domain — with or without www. You can paste a full web address too.'}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </Panel>
  );
}

const DomainAdmin = () => {
  useAdminPageTitle('Domain & Web Address');
  const { orgId, org } = useAdminOrg();
  const { canWrite, assertCanWrite } = useReadOnlyGuard();
  const { toast } = useToast();
  const copy = useCopy();
  const [deleteTarget, setDeleteTarget] = useState<CustomDomain | null>(null);

  const { data: domains = [], isLoading } = useDomainsData(orgId);
  const entitlement = useCustomDomainEntitlement(orgId);
  const addMutation = useAddDomain(orgId);
  const removeMutation = useRemoveDomain(orgId);

  const subdomainUrl = org.slug ? `${org.slug}.thepolishedversion.com` : null;
  const allowed = entitlement.data === true;

  const handleAdd = async (domain: string) => {
    if (!assertCanWrite()) return;
    try {
      await addMutation.mutateAsync(domain);
      toast({ title: 'Domain added', description: 'Now add the DNS records shown below.' });
    } catch (err: unknown) {
      if (err instanceof PlanUpgradeRequiredError) void entitlement.refetch();
      toast({ title: 'Couldn’t add that domain', description: getErrorMessage(err), variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !assertCanWrite()) return;
    try {
      const res = await removeMutation.mutateAsync(deleteTarget.id);
      toast({ title: 'Domain removed', description: res.detachWarning ?? 'Your thepolishedversion.com address still works.' });
    } catch (err: unknown) {
      toast({ title: 'Couldn’t remove the domain', description: getErrorMessage(err), variant: 'destructive' });
    }
    setDeleteTarget(null);
  };

  if (isLoading || entitlement.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AdminPage width="narrow" className="max-w-3xl">
      <PageHeader description="Choose where customers find your website." />

      <Panel
        title="Your free address"
        icon={<Globe className="h-4 w-4 text-muted-foreground" />}
        description="Always works, even after you connect your own domain."
      >
        {subdomainUrl ? (
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md bg-muted px-3 py-2 font-mono text-[13px]">{subdomainUrl}</code>
            <Button variant="ghost" size="sm" className={adminButton.iconGhost} onClick={() => copy(`https://${subdomainUrl}`, 'Address copied')} aria-label="Copy your free address">
              <Copy />
            </Button>
            <Button variant="ghost" size="sm" className={adminButton.iconGhost} asChild>
              <a href={`https://${subdomainUrl}`} target="_blank" rel="noreferrer" aria-label="Open your site"><ExternalLink /></a>
            </Button>
          </div>
        ) : (
          <p className="text-[13px] text-muted-foreground">No address set for this organisation yet.</p>
        )}
      </Panel>

      {domains.length === 0 && !allowed && <UpgradePanel />}
      {domains.length === 0 && allowed && (
        <AddDomainForm onAdd={handleAdd} saving={addMutation.isPending} disabled={!canWrite} />
      )}

      {domains.map((d) => (
        <DomainCard key={d.id} orgId={orgId!} domain={d} canWrite={canWrite} onRemove={() => setDeleteTarget(d)} />
      ))}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this domain?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono">{deleteTarget?.domain}</span> will stop showing your site. Your free
              thepolishedversion.com address keeps working. You can remove the DNS records afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={removeMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeMutation.isPending ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminPage>
  );
};

export default DomainAdmin;
